import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SaleItemInterface } from "../../types/types";
import {
  addToCart,
  increaseQuantity,
  decreaseQuantity,
  removeFromCart,
} from "../../../store/slices/cartSlice";
import { RootState } from "../../../store/store";
import { selectForceStores } from "../../../store/slices/storeListSlice";
import { selectCurrentCluster } from "../../../store/slices/currentClusterSlice";
import { checkItemAvailability } from "../../utils/functions/checkItemAvailability";
import { useDisplayPrice } from "../../hooks/useDisplayPrice";

interface UseItemRelationsProps {
  addons?: Array<{ addon: SaleItemInterface }> | null;
  recommendations?: Array<{ recommended: SaleItemInterface }> | null;
}

export const useItemRelations = ({
  addons,
  recommendations,
}: UseItemRelationsProps) => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const selectedStoreIds = useSelector(selectForceStores);
  const currentCluster = useSelector(selectCurrentCluster);
  const forceStore = currentCluster?.forceStore ?? false;

  const getPrice = useDisplayPrice();

  const addonItems = useMemo((): SaleItemInterface[] => {
    if (!addons || !Array.isArray(addons)) return [];

    const items = addons
      .map((item) => item?.addon)
      .filter(
        (item): item is SaleItemInterface =>
          item !== null && item !== undefined,
      );

    if (!forceStore) return items;

    return items.filter(
      (item) =>
        checkItemAvailability({
          item,
          forceStore,
          selectedStoreIds,
        }).available,
    );
  }, [addons, forceStore, selectedStoreIds]);

  const recommendationItems = useMemo((): SaleItemInterface[] => {
    if (!recommendations || !Array.isArray(recommendations)) return [];

    const items = recommendations
      .map((item) => item?.recommended)
      .filter(
        (item): item is SaleItemInterface =>
          item !== null && item !== undefined,
      );

    if (!forceStore) return items;

    return items.filter(
      (item) =>
        checkItemAvailability({
          item,
          forceStore,
          selectedStoreIds,
        }).available,
    );
  }, [recommendations, forceStore, selectedStoreIds]);

  const getItemQuantity = (itemId: number): number => {
    const cartItem = cartItems.find((item) => item.id === String(itemId));
    return cartItem?.quantity || 0;
  };

  const handleAddToCart = (item: SaleItemInterface) => {
    const price = getPrice(item.prices);
    dispatch(
      addToCart({
        id: String(item.id),
        name: item.name,
        price,
        image: item.files?.[0] || undefined,
        minQuantity: item.minQuantity ?? 1,
        quantityStep: item.quantityStep ?? 1,
        attributes: item.attributes || [],
        unit: item.unit,
      }),
    );
  };

  const handleIncrease = (itemId: number) => {
    dispatch(increaseQuantity(String(itemId)));
  };

  const handleDecrease = (itemId: number) => {
    dispatch(decreaseQuantity(String(itemId)));
  };

  const handleRemove = (itemId: number) => {
    dispatch(removeFromCart(String(itemId)));
  };

  const hasAddons = addonItems.length > 0;
  const hasRecommendations = recommendationItems.length > 0;

  return {
    addonItems,
    recommendationItems,
    hasAddons,
    hasRecommendations,
    getItemQuantity,
    handleAddToCart,
    handleIncrease,
    handleDecrease,
    handleRemove,
  };
};
